import { Dialog, Box, Typography } from '@mui/material';
import React from 'react';
import { updateBankModalStyles } from './UpdateBankModal.styles';
import { useTranslation } from 'react-i18next';

interface LinkedFirm {
  customerId: string;
  firmName: string;
}

interface UpdateBankModalProps {
  open: boolean;
  onClose: () => void;
  onUploadPhoto: () => void;
  onTakePhoto: () => void;
  panNumber?: string;
  linkedFirms?: LinkedFirm[];
  currentCustomerId?: string;
  currentCustomerShopName?: string;
}

export function UpdateBankModal({ open, onClose, onUploadPhoto, onTakePhoto, panNumber, linkedFirms, currentCustomerId, currentCustomerShopName }: UpdateBankModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: updateBankModalStyles.paper }}>
      <Box sx={updateBankModalStyles.container}>
        <Box sx={updateBankModalStyles.header}>
          <Box sx={updateBankModalStyles.titleWrap}>
            <Typography sx={updateBankModalStyles.title}>
              {t('update_bank_details', { defaultValue: 'Update Bank Details' })}
            </Typography>
          </Box>
          <Box onClick={onClose} sx={updateBankModalStyles.closeButton}>
            <svg fill="none" viewBox="0 0 14 14" width="14" height="14" style={{ margin: '20.83%' }}>
              <path
                d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
                fill="black"
                fillOpacity="0.56"
              />
            </svg>
          </Box>
        </Box>

        {panNumber && (linkedFirms && linkedFirms.length > 0 || currentCustomerId) && (
          <Box sx={updateBankModalStyles.descriptionSection}>
            <Typography sx={updateBankModalStyles.descriptionText}>
              {t('update_bank_linked_pan_text', { defaultValue: 'Updating bank details will also apply to other firms linked to PAN' })}{' '}
              <Typography component="span" sx={updateBankModalStyles.descriptionBold}>
                {panNumber}:
              </Typography>
            </Typography>
            <Box component="ul" sx={updateBankModalStyles.linkedFirmsList}>
              {currentCustomerId && (
                <li key={currentCustomerId}>
                  {t('customer_id_label', { defaultValue: 'Customer ID' })}: {currentCustomerId}{currentCustomerShopName ? ` (${currentCustomerShopName})` : ''}
                </li>
              )}
              {linkedFirms && linkedFirms.map((firm) => (
                <li key={firm.customerId}>
                  {t('customer_id_label', { defaultValue: 'Customer ID' })}: {firm.customerId}{firm.firmName ? ` (${firm.firmName})` : ''}
                </li>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={updateBankModalStyles.noteRow}>
          <Box sx={updateBankModalStyles.noteIcon}>
            <svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="#005E62" />
            </svg>
          </Box>
          <Typography sx={updateBankModalStyles.noteText}>
            {t('update_bank_note', { defaultValue: 'Note: Already initiated payouts will go to your existing account. Future payouts will be credited to the new account details.' })}
          </Typography>
        </Box>

        <Box sx={updateBankModalStyles.actions}>
          <Box onClick={onUploadPhoto} sx={updateBankModalStyles.actionButton}>
            <Box sx={updateBankModalStyles.actionButtonInner}>
              <Box sx={{ width: 18, minWidth: 18, height: 24, position: 'relative', flexShrink: 0 }}>
                <Box sx={{ position: 'absolute', left: 'calc(50% - 2px)', top: '50%', transform: 'translate(-50%, -50%)', width: 24, height: 24, overflow: 'hidden' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: 'calc(50% - 1px)', top: 'calc(50% - 1px)', transform: 'translate(-50%, -50%)' }}>
                    <path d="M9 4.5C9 6.98528 6.98528 9 4.5 9C2.01472 9 0 6.98528 0 4.5C0 2.01472 2.01472 0 4.5 0C6.98528 0 9 2.01472 9 4.5ZM5 2.5C5 2.22386 4.77614 2 4.5 2C4.22386 2 4 2.22386 4 2.5V4H2.5C2.22386 4 2 4.22386 2 4.5C2 4.77614 2.22386 5 2.5 5H4L4 6.5C4 6.77614 4.22386 7 4.5 7C4.77614 7 5 6.77614 5 6.5V5H6.5C6.77614 5 7 4.77614 7 4.5C7 4.22386 6.77614 4 6.5 4H5V2.5ZM13 3H9.79297C9.69436 2.65136 9.56223 2.31679 9.40029 2H13C14.6569 2 16 3.34315 16 5V13C16 14.6569 14.6569 16 13 16H5C3.34315 16 2 14.6569 2 13V9.40029C2.31679 9.56223 2.65136 9.69436 3 9.79297V13C3 13.3726 3.10191 13.7215 3.27937 14.0201L7.94868 9.43203C8.53227 8.85859 9.46773 8.85859 10.0513 9.43203L14.7206 14.0201C14.8981 13.7215 15 13.3726 15 13V5C15 3.89543 14.1046 3 13 3ZM13 6.5C13 7.32843 12.3284 8 11.5 8C10.6716 8 10 7.32843 10 6.5C10 5.67157 10.6716 5 11.5 5C12.3284 5 13 5.67157 13 6.5ZM12 6.5C12 6.22386 11.7761 6 11.5 6C11.2239 6 11 6.22386 11 6.5C11 6.77614 11.2239 7 11.5 7C11.7761 7 12 6.77614 12 6.5ZM3.98824 14.7256C4.28505 14.9 4.63085 15 5 15H13C13.3692 15 13.7149 14.9 14.0118 14.7256L9.35044 10.1453C9.15591 9.95417 8.84409 9.95417 8.64956 10.1453L3.98824 14.7256Z" fill="#007CBF" />
                  </svg>
                </Box>
              </Box>
              <Typography sx={updateBankModalStyles.actionButtonText}>
                {t('upload_photo', { defaultValue: 'Upload Photo' })}
              </Typography>
            </Box>
          </Box>

          <Box onClick={onTakePhoto} sx={updateBankModalStyles.actionButton}>
            <Box sx={updateBankModalStyles.actionButtonInner}>
              <Box sx={{ width: 18, minWidth: 18, height: 24, position: 'relative', flexShrink: 0 }}>
                <Box sx={{ position: 'absolute', left: 'calc(50% - 2px)', top: '50%', transform: 'translate(-50%, -50%)', width: 24, height: 24, overflow: 'hidden' }}>
                  <svg width="17" height="17" viewBox="0 0 16.9953 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: 'calc(50% + 0.5px)', top: 'calc(50% + 0.5px)', transform: 'translate(-50%, -50%)' }}>
                    <path d="M6.11924 0C5.55219 0 5.0336 0.319766 4.77897 0.826432L4.18948 1.9994H2.5C1.11929 1.9994 0 3.11868 0 4.4994V12.5C0 13.8807 1.11929 15 2.5 15H7.59501C7.43307 14.6832 7.30094 14.3486 7.20233 14H2.5C1.67157 14 1 13.3284 1 12.5V4.4994C1 3.67097 1.67157 2.9994 2.5 2.9994H4.49779C4.68681 2.9994 4.85967 2.89281 4.94455 2.72392L5.67248 1.27548C5.75736 1.10659 5.93022 1 6.11924 1H9.88344C10.0728 1 10.246 1.10702 10.3307 1.27643L11.0538 2.72296C11.1385 2.89238 11.3116 2.9994 11.501 2.9994H13.5C14.3284 2.9994 15 3.67097 15 4.4994V7.60212C15.3578 7.78548 15.693 8.0069 16 8.26105V4.4994C16 3.11869 14.8807 1.9994 13.5 1.9994H11.8101L11.2251 0.829302C10.9711 0.321059 10.4516 0 9.88344 0H6.11924ZM7.9953 4.00003C9.87145 4.00003 11.4459 5.29169 11.8779 7.03426C11.5408 7.07192 11.213 7.14004 10.8971 7.23581C10.5591 5.94911 9.38805 5.00003 7.9953 5.00003C6.33845 5.00003 4.9953 6.34318 4.9953 8.00003C4.9953 9.39279 5.94439 10.5639 7.2311 10.9018C7.13533 11.2177 7.06721 11.5456 7.02956 11.8827C5.28698 11.4507 3.9953 9.87619 3.9953 8.00003C3.9953 5.79089 5.78616 4.00003 7.9953 4.00003ZM16.9953 12.5C16.9953 14.9853 14.9806 17 12.4953 17C10.01 17 7.9953 14.9853 7.9953 12.5C7.9953 10.0147 10.01 8 12.4953 8C14.9806 8 16.9953 10.0147 16.9953 12.5ZM12.9953 10.5C12.9953 10.2239 12.7714 10 12.4953 10C12.2192 10 11.9953 10.2239 11.9953 10.5V12H10.4953C10.2192 12 9.9953 12.2239 9.9953 12.5C9.9953 12.7761 10.2192 13 10.4953 13H11.9953V14.5C11.9953 14.7761 12.2192 15 12.4953 15C12.7714 15 12.9953 14.7761 12.9953 14.5V13H14.4953C14.7714 13 14.9953 12.7761 14.9953 12.5C14.9953 12.2239 14.7714 12 14.4953 12H12.9953V10.5Z" fill="#007CBF" />
                  </svg>
                </Box>
              </Box>
              <Typography sx={updateBankModalStyles.actionButtonText}>
                {t('take_photo', { defaultValue: 'Take Photo' })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
